import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, X, BookmarkPlus } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { useAuth } from '../../context/AuthContext';
import logger from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SAVEABLE_PATHS = new Set([
  '/work-orders', '/assets', '/inspections', '/operations',
  '/vendors', '/ppm', '/budget',
]);

const FILTER_USE_DEBOUNCE_MS = 1500;

/**
 * Watches the current URL on saveable list pages, debounces filter changes,
 * bumps the per-user filter-use counter, and surfaces a "Save this view?"
 * suggestion banner once the same combo has been used 5+ times.
 */
const SmartSuggestionBanner = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [suggestion, setSuggestion] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return new Set(JSON.parse(sessionStorage.getItem('fp_dismissed_suggestions') || '[]'));
    } catch { return new Set(); }
  });
  const debounceRef = useRef(null);
  const lastReportedRef = useRef('');

  const onSaveable = SAVEABLE_PATHS.has(location.pathname);
  const search = (location.search || '').replace(/^\?/, '');
  const key = `${location.pathname}?${search}`;

  // Debounced bump on filter change
  useEffect(() => {
    if (!user || !onSaveable || !search) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (lastReportedRef.current === key) return;
      lastReportedRef.current = key;
      try {
        const token = localStorage.getItem('token');
        await axios.post(
          `${API_URL}/api/filter-use`,
          { path: location.pathname, query_string: search },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (err) { logger.debug('filter-use bump failed', err); }
    }, FILTER_USE_DEBOUNCE_MS);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [key, onSaveable, user, search, location.pathname]);

  // Poll suggestions every minute (and on mount)
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const fetchSuggestions = async () => {
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(
          `${API_URL}/api/filter-use/suggestions?threshold=5`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (cancelled) return;
        const fresh = (data || []).find((s) => !dismissed.has(s.key));
        setSuggestion(fresh || null);
      } catch (err) { logger.debug('suggestions fetch failed', err); }
    };
    fetchSuggestions();
    const intv = setInterval(fetchSuggestions, 60_000);
    return () => { cancelled = true; clearInterval(intv); };
  }, [user, dismissed]);

  if (!suggestion) return null;

  const dismiss = async (suppressForever = false) => {
    const next = new Set(dismissed);
    next.add(suggestion.key);
    setDismissed(next);
    try {
      sessionStorage.setItem('fp_dismissed_suggestions', JSON.stringify([...next]));
    } catch { /* ignore */ }
    if (suppressForever) {
      try {
        const token = localStorage.getItem('token');
        await axios.post(
          `${API_URL}/api/filter-use/dismiss`,
          { path: suggestion.path, query_string: suggestion.query_string },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (err) { logger.debug('dismiss failed', err); }
    }
    setSuggestion(null);
  };

  const saveIt = async () => {
    // Navigate to the filter combo so the proper "Save this view" dialog in the
    // TopBar's SavedViewsMenu opens against fresh, in-context filters. This
    // keeps the input UX consistent (validated dialog, not window.prompt).
    navigate(`${suggestion.path}?${suggestion.query_string}`);
    // Mark this combo dismissed so the banner doesn't reappear immediately
    dismiss(false);
    // Hint: open the Views popover next paint. Picked up by SavedViewsMenu via custom event.
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('fp:open-save-view'));
    }, 300);
  };

  return (
    <Card
      className="fixed bottom-6 right-6 max-w-sm z-40 border-[#111565]/30 shadow-lg p-4 bg-white"
      data-testid="smart-suggestion-banner"
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-9 h-9 rounded-full bg-[#111565]/10 flex items-center justify-center">
          <Sparkles className="h-4 w-4 text-[#111565]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium leading-tight">Save this view?</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            You've used <code className="px-1 bg-muted rounded text-[10px]">{decodeURIComponent(suggestion.query_string)}</code>{' '}
            on <strong>{suggestion.path.replace('/', '')}</strong> {suggestion.uses}× recently.
          </p>
          <div className="flex items-center gap-2 mt-3">
            <Button
              size="sm"
              className="h-7 bg-[#111565] hover:bg-[#111565]/90 text-xs gap-1"
              onClick={saveIt}
              data-testid="suggestion-save-btn"
            >
              <BookmarkPlus className="h-3.5 w-3.5" /> Save view
            </Button>
            <Button
              size="sm" variant="ghost" className="h-7 text-xs"
              onClick={() => dismiss(false)}
              data-testid="suggestion-dismiss-btn"
            >
              Not now
            </Button>
            <Button
              size="sm" variant="ghost" className="h-7 text-xs text-muted-foreground"
              onClick={() => dismiss(true)}
              data-testid="suggestion-dismiss-forever-btn"
            >
              Don't ask again
            </Button>
          </div>
        </div>
        <button
          type="button"
          onClick={() => dismiss(false)}
          className="text-muted-foreground hover:text-foreground"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </Card>
  );
};

export default SmartSuggestionBanner;
